import { API, unwrapData } from '../axios-instance'
import { CursorResult, PageResult } from '../shared/types'
import {
  Account,
  AddRemovePointsParams,
  AllocationForecast,
  AllocationForecastParams,
  BudgetItem,
  BudgetItemEstimate,
  BudgetItemUsage,
  BudgetItemsEmbeds,
  BudgetSetting,
  BudgetSettingParams,
  CreateBudgetItemParams,
  EstimateBudgetItemParams,
  GetBudgetSettingsParams,
  ListAccountsParams,
  ListBudgetItemOwnersParams,
  ListBudgetItemOwnersResults,
  ListBudgetItemsParams,
  ListPointsBanksParams,
  ListTransactionRecipientsCursorParams,
  ListTransactionRecipientsParams,
  PointsBankOption,
  RedeemableAccountsParams,
  RedeemablePointsParams,
  ReverseTransactionParams,
  TotalRedeemablePoints,
  Transaction,
  TransactionRecipient,
} from './types'

export const listAccounts = async (params: ListAccountsParams, tenantMemberId?: string) => {
  const rootPath = !!tenantMemberId
    ? `/points/accounts/owner/${tenantMemberId}`
    : '/points/accounts/my'
  return await API.get<PageResult<Account>>(rootPath, { params }).then(unwrapData)
}

export const getBudgetSettings = async (params: GetBudgetSettingsParams) => {
  return await API.get<BudgetSetting>('/points/budgetSettings', { params }).then(unwrapData)
}

export const updateBudgetSettings = async (params: BudgetSettingParams) => {
  return await API.put<BudgetSetting>('/points/budgetSettings', params).then(unwrapData)
}

export const createBudgetItem = async (formData: CreateBudgetItemParams) => {
  return await API.post<BudgetItem>('/points/budgetItems', {
    ...formData,
  }).then(unwrapData)
}

export const listBudgetItems = async (params: ListBudgetItemsParams) => {
  return await API.get<PageResult<BudgetItem>>('/points/budgetItems', { params }).then(unwrapData)
}

export const deleteBudgetItem = async (budgetItemId: string) => {
  return await API.delete<boolean>(`/points/budgetItems/${budgetItemId}`).then(unwrapData)
}

export const getBudgetItem = async (budgetItemId: string, embeds?: BudgetItemsEmbeds[]) => {
  return await API.get<PageResult<BudgetItem>>('/points/budgetItems', {
    params: {
      page: 1,
      pageSize: 1,
      idIn: [budgetItemId],
      embeds,
    } as ListBudgetItemsParams,
  })
    .then(unwrapData)
    .then(data => data.results[0])
}

export const updateBudgetItem = async (budgetItemId: string, formdata: CreateBudgetItemParams) => {
  return await API.put<BudgetItem>(`/points/budgetItems/${budgetItemId}`, {
    ...formdata,
  }).then(unwrapData)
}

export const listBudgetItemOwners = async (params: ListBudgetItemOwnersParams) => {
  return await API.get<ListBudgetItemOwnersResults>('/points/budgetItems/owners', {
    params,
  }).then(unwrapData)
}

export const listTransactionRecipientsCursor = async (
  params: ListTransactionRecipientsCursorParams,
) => {
  return await API.get<CursorResult<TransactionRecipient>>('/points/transactionRecipients', {
    params,
  }).then(unwrapData)
}

export const estimateBudgetItem = async (params: EstimateBudgetItemParams) => {
  return await API.post<BudgetItemEstimate>('/points/budgetItems/estimate', params).then(unwrapData)
}

export const listPointsBanks = async (params: ListPointsBanksParams) => {
  return await API.get<PageResult<PointsBankOption>>('/points/pointsBanks', { params }).then(
    unwrapData,
  )
}

export const getTotalRedeemablePoints = async (params: RedeemablePointsParams) => {
  return await API.get<TotalRedeemablePoints>('/points/accounts/redeemable/total', {
    params: { includeLocked: false, ...params },
  }).then(unwrapData)
}

export const listRedeemableAccounts = async (params: RedeemableAccountsParams) => {
  return await API.get<PageResult<Account>>('/points/accounts/redeemable', { params }).then(
    unwrapData,
  )
}

export const exportRedeemableAccounts = async (
  params?: RedeemableAccountsParams,
): Promise<BlobPart> => {
  return await API.get<BlobPart>('/points/accounts/redeemable/export', {
    params,
    responseType: 'blob',
  }).then(unwrapData)
}

export const reverseTransaction = async (
  transactionId: string,
  accountId: string,
  params: ReverseTransactionParams,
) => {
  return await API.delete<TransactionRecipient>(
    `/points/transactions/${transactionId}/account/${accountId}`,
    { data: params },
  ).then(unwrapData)
}

export const addRedeemablePoints = async (accountId: string, params: AddRemovePointsParams) => {
  return await API.post<Transaction>(
    `/points/accounts/redeemable/${accountId}/points`,
    params,
  ).then(unwrapData)
}

export const removeRedeemablePoints = async (accountId: string, params: AddRemovePointsParams) => {
  return await API.delete<Transaction>(`/points/accounts/redeemable/${accountId}/points`, {
    data: params,
  }).then(unwrapData)
}

export const addAllocationPoints = async (accountId: string, params: AddRemovePointsParams) => {
  return await API.post<Transaction>(
    `/points/accounts/allocation/${accountId}/points`,
    params,
  ).then(unwrapData)
}

export const removeAllocationPoints = async (accountId: string, params: AddRemovePointsParams) => {
  return await API.delete<Transaction>(`/points/accounts/allocation/${accountId}/points`, {
    data: params,
  }).then(unwrapData)
}

export const getAllocationForecast = async (memberId: string, params: AllocationForecastParams) => {
  return await API.get<AllocationForecast[]>(`/points/budgetItems/members/${memberId}/forecast`, {
    params,
  }).then(unwrapData)
}

export const exportTransactionRecipients = async (params: ListTransactionRecipientsParams) => {
  return await API.post<BlobPart>('/points/transactionRecipients/csv', params, {
    params: { sorting: params.sorting },
  }).then(unwrapData)
}

export const resetRedeemablePoints = async () => {
  return await API.post<boolean>('/points/accounts/payout/expire').then(unwrapData)
}

export const resetAllocationPoints = async () => {
  return await API.post<boolean>('/points/accounts/fund/allocate').then(unwrapData)
}

export const listTenantAccounts = async () => {
  return await API.get<PageResult<Account>>('/points/accounts/tenant').then(unwrapData)
}

export const disablePoints = async () => {
  return await API.post<boolean>('/points/accounts/fund/disable').then(unwrapData)
}

export const enablePoints = async () => {
  return await API.post<boolean>('/points/accounts/fund/enable').then(unwrapData)
}

export const getBudgetItemUsage = async (budgetItemId: string) => {
  return await API.get<BudgetItemUsage>(`/points/budgetItems/${budgetItemId}/usage`).then(
    unwrapData,
  )
}

// delegation
export const getDelegatorPoints = async (delegatorId: string) => {
  return await API.get<Account[]>(`/points/accounts/delegator/${delegatorId}`).then(unwrapData)
}
